import React, { useState, useEffect } from 'react'
import { Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { omitBy } from 'lodash'
import PropTypes from 'prop-types'
import * as Yup from 'yup'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { Button, Input, Form, Checkbox } from 'components/Forms'
import { Alert } from 'ui'
import { closeModal } from 'store/actions/modals'
import { createErrorNotification } from 'store/actions/notifications'
import { refreshCounter } from 'store/actions/refresh'
import { Card } from 'ui'

import { t } from 'translations'

// Maps to which url to use for delete and check what is being deleted
const resourceMap = {
  teams: 'teams/teams',
  tags: 'tags/tags',
  positions: 'structure/structure'
}

const DeleteAffectsConfirmationModal = ({ _id, name }) => {
  const dispatch = useDispatch()

  const [removeCount, setRemoveCount] = useState({})
  const [errorLoading, setErrorLoading] = useState()

  // Will trigget the fetch of additional information
  useEffect(() => {
    const fetchAmountLost = async () => {
      // Fetch the information about what will be lost
      const { success, result } = await Api.get(`${resourceMap[name]}/removeCount/${_id}`)

      if (!success || typeof result !== 'object') {
        setErrorLoading(true)
        return dispatch(createErrorNotification())
      } else {
        // Remove null values
        const cleanedResult = omitBy(result, value => value === 0)
        setRemoveCount(cleanedResult)
      }
    }

    fetchAmountLost()
  }, [_id, dispatch, name, setRemoveCount])

  const remove = async actions => {
    const { success } = await Api.delete(`${resourceMap[name]}?id=${_id}`)
    if (success) {
      dispatch(refreshCounter(name))
      dispatch(closeModal())
    } else {
      actions.setSubmitting(false)
      actions.setStatus({ type: 'danger', msg: 'administration.confirmDeletion.ERROR_DELETING' })
    }
  }

  return (
    <BaseModal destructive title={`administration.${name}.DELETE_BUTTON`} hideButtons>
      {errorLoading ? (
        <>
          <Alert variant="warning" className="mt-2">
            {t('administration.confirmDeletion.ERROR_LOADING')}
          </Alert>
          <Button text="actions.CLOSE" className="btn btn-outline-secondary mt-2" onClick={() => dispatch(closeModal())} />
        </>
      ) : (
        <Formik
          enableReinitialize={true}
          validationSchema={Yup.object().shape({
            delete: Yup.string()
              .test('DELETE', t(`administration.${name}.DELETE_BUTTON.BAD_MATCH`), value => value?.toLowerCase() === t('actions.DELETE').toLowerCase())
              .required(`administration.${name}.DELETE_BUTTON.FIELD_IS_REQUIRED`)
          })}
          initialValues={{ delete: '' }}
          onSubmit={remove}
        >
          {({ status, isSubmitting, setStatus, values }) => {
            const canDelete = Object.keys(removeCount).every(resource => values[resource]) && values.delete?.toLowerCase() === t('actions.DELETE').toLowerCase()
            return (
              <Form className="my-2">
                {!!Object.keys(removeCount).length && (
                  <Card className="bg-light my-2">
                    <Card.Body>
                      <Trans t={t} i18nKey={'administration.confirmDeletion.WILL_BE_IMPACTED'} components={{ h6: <h6 /> }} />
                      <ul className="mt-3">
                        {Object.keys(removeCount).map(resourceName => {
                          return (
                            <Checkbox
                              key={`${resourceName}`}
                              name={`${resourceName}`}
                              noDescription
                              label={' '}
                              required
                              customLabelElement={
                                <Trans
                                  t={t}
                                  count={removeCount[resourceName]}
                                  i18nKey={`administration.confirmDeletion.AFFECTED_${resourceName.toUpperCase()}`}
                                >
                                  <b></b>
                                </Trans>
                              }
                            />
                          )
                        })}
                      </ul>
                    </Card.Body>
                  </Card>
                )}

                {/* Error message */}
                <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                  {status?.msg}
                </Alert>
                <div className="mb-3"></div>
                <Input module={`administration.${name}.DELETE_BUTTON`} name="delete" required />
                <Alert variant="danger">
                  <Trans t={t} i18nKey={'administration.confirmDeletion.WARNING'}>
                    <b></b>
                  </Trans>
                </Alert>
                <Button
                  className="btn btn-danger mr-2"
                  type="submit"
                  isSubmitting={isSubmitting}
                  disabled={!canDelete}
                  text={`administration.${name}.DELETE_BUTTON.PRIMARY_ACTION`}
                />
                <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                  {t(`actions.CLOSE`)}
                </Button>
              </Form>
            )
          }}
        </Formik>
      )}
    </BaseModal>
  )
}

export default DeleteAffectsConfirmationModal

DeleteAffectsConfirmationModal.propTypes = {
  _id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
}
